import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE } from '../api-base.token';
import { AddZonesRequest } from './add-zones-request';
import { InspectionDefinitionRequest } from './inspection-definition-request';
import { InspectionDefinitionResponse } from './inspection-definition-response';
import { InspectionTemplateRequest } from './inspection-template-request';
import { RenameInspectionDefinitionRequest } from './rename-inspection-definition-request';

const path = 'inspection-definitions';

@Injectable({
  providedIn: 'root',
})
export class InspectionDefinitionsHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE)
    private readonly apiBase: string
  ) {}

  getAll(): Observable<InspectionDefinitionResponse[]> {
    const url = Location.joinWithSlash(this.apiBase, path);

    return this.httpClient.get<InspectionDefinitionResponse[]>(url);
  }

  getTemplates(): Observable<InspectionDefinitionResponse[]> {
    const url = Location.joinWithSlash(this.apiBase, `${path}/templates`);

    return this.httpClient.get<InspectionDefinitionResponse[]>(url);
  }

  saveInspectionAsTemplate(
    inspectionTemplateRequest: InspectionTemplateRequest
  ): Observable<InspectionDefinitionResponse> {
    const url = Location.joinWithSlash(this.apiBase, `${path}/templates`);

    return this.httpClient.post<InspectionDefinitionResponse>(
      url,
      inspectionTemplateRequest
    );
  }

  unSaveInspectionAsTemplate(
    inspectionDefinitionId: string
  ): Observable<InspectionDefinitionResponse> {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/templates/${inspectionDefinitionId}/unpublish`
    );

    return this.httpClient.post<InspectionDefinitionResponse>(url, {});
  }

  get(inspectionDefinitionId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${inspectionDefinitionId}`
    );

    return this.httpClient.get<InspectionDefinitionResponse>(url);
  }

  getLatestVersion(rootInspectionDefinitionIndex: number) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/Latest/${rootInspectionDefinitionIndex}`
    );

    return this.httpClient.get<InspectionDefinitionResponse>(url);
  }

  getLatestVersionById(rootInspectionDefinitionId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/LatestById/${rootInspectionDefinitionId}`
    );

    return this.httpClient.get<InspectionDefinitionResponse>(url);
  }

  post(
    inspectionDefinitionRequest: InspectionDefinitionRequest
  ): Observable<InspectionDefinitionResponse> {
    const url = Location.joinWithSlash(this.apiBase, path);

    return this.httpClient.post<InspectionDefinitionResponse>(
      url,
      inspectionDefinitionRequest
    );
  }

  put(
    rootInspectionDefinitionIndex: number,
    inspectionDefinitionRequest: InspectionDefinitionRequest
  ): Observable<InspectionDefinitionResponse> {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${rootInspectionDefinitionIndex}`
    );

    return this.httpClient.put<InspectionDefinitionResponse>(
      url,
      inspectionDefinitionRequest
    );
  }

  delete(inspectionDefinitionId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${inspectionDefinitionId}`
    );

    return this.httpClient.delete(url);
  }

  addZones(inspectionDefinitionId: string, zoneIds: string[]) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${inspectionDefinitionId}/zones`
    );

    const request: AddZonesRequest = {
      zoneIds,
    };

    return this.httpClient.post(url, request);
  }

  deleteZone(inspectionDefinitionId: string, zoneId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${inspectionDefinitionId}/zones/${zoneId}`
    );

    return this.httpClient.delete(url);
  }

  archive(
    inspectionDefinitionId: string
  ): Observable<InspectionDefinitionResponse> {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${inspectionDefinitionId}/archive`
    );

    return this.httpClient.post<InspectionDefinitionResponse>(url, {});
  }

  activate(
    inspectionDefinitionId: string
  ): Observable<InspectionDefinitionResponse> {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${inspectionDefinitionId}/activate`
    );

    return this.httpClient.post<InspectionDefinitionResponse>(url, {});
  }

  rename(
    inspectionDefinitionId: string,
    renameInspectionDefinitionRequest: RenameInspectionDefinitionRequest
  ): Observable<InspectionDefinitionResponse> {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${inspectionDefinitionId}/rename`
    );

    return this.httpClient.post<InspectionDefinitionResponse>(
      url,
      renameInspectionDefinitionRequest
    );
  }
}
